import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { WRONG_MEASUREMENT_VALUE } from '../../../../../../../const';
import { ChannelType } from '../../../../../types';
import { TariffForm } from '../../../../common/tariff';
import { useOptimizerConfigurationContext } from '../../context/configuration-context';
import { OptimizerConfigurationHeaterPowerLimit } from '../components/heater-power-limit';
import { OptimizerConfigurationPhaseVoltageThreshold } from '../components/phase-voltage-threshold';
import { OptimizerConfigurationRelays } from '../components/relays';
import { TempWarningDialog } from '../components/temp-warning-dialog';
import { OptimizerConfigurationTemperature } from '../components/temperature';
import { useOptimizerComfort } from '../hooks/use-optimizer-comfort';
import { useOptimizerSetData } from '../hooks/use-optimizer-set-data';
import { useOptimizerSupport } from '../hooks/use-optimizer-support';
import { ComfortFormBody, initialRelay } from '../types';
import { checkIfTempMinWarning } from '../utils';

type OptimizerConfigurationWorkModeComfortProps = {
  channel: ChannelType;
  temperature?: number;
};

export const OptimizerConfigurationWorkModeComfort: React.FC<OptimizerConfigurationWorkModeComfortProps> = ({
  channel,
  temperature,
}) => {
  const { t: tc } = useTranslation('common');
  const { submitConfiguration } = useOptimizerComfort(channel);
  const [tempWarningDialog, setTempWarningDialog] = useState<boolean>(false);
  const { selectedWorkMode } = useOptimizerConfigurationContext();
  const form = useForm<ComfortFormBody>({
    defaultValues: {
      tempMin: 0,
      tempMax: 0,
      heaterPowerLimit: 0,
      voltageThreshold1: 0,
      voltageThreshold2: 0,
      voltageThreshold3: 0,
      relayConfig1: { ...initialRelay, index: 1 },
      relayConfig2: { ...initialRelay, index: 2 },
      relayConfig3: { ...initialRelay, index: 3 },
      operator: '',
      tariffName: '',
      wholeWeekendCheaper: false,
      wholeHolidaysCheaper: false,
      ignoreSummerTimeChanges: false,
      hoursRanges: [],
    },
  });
  const { tariffSupported } = useOptimizerSupport(channel);
  const { setOptimizerData } = useOptimizerSetData(form.setValue, 'comfort');

  useEffect(() => {
    if (channel) setOptimizerData(channel);
  }, [channel]);

  const handleConfirmation = () => {
    const values = form.getValues();
    submitConfiguration(selectedWorkMode, values, () => setTempWarningDialog(false));
  };

  const onSubmit = form.handleSubmit((values) => {
    if (checkIfTempMinWarning(values.tempMin)) {
      setTempWarningDialog(true);
      return;
    }

    submitConfiguration(selectedWorkMode, values);
  });

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <div
            className={classNames('optimizer-configuration-form__item', {
              disabled: temperature === WRONG_MEASUREMENT_VALUE,
            })}
          >
            <OptimizerConfigurationTemperature minTemperatureExists />
          </div>

          <hr className="m-t-24 m-b-24" />

          <div className="optimizer-configuration-form__item">
            <OptimizerConfigurationHeaterPowerLimit />
          </div>

          {tariffSupported && (
            <>
              <hr className="m-t-24 m-b-24" />

              <div className="optimizer-configuration-form__item">
                <TariffForm dynamicTariff={false} />
              </div>
            </>
          )}

          <hr className="m-t-24 m-b-24" />

          <div className="optimizer-configuration-form__item">
            <OptimizerConfigurationPhaseVoltageThreshold />
          </div>

          <hr className="m-t-24 m-b-24" />

          <div className="optimizer-configuration-form__item">
            <OptimizerConfigurationRelays />
          </div>

          <div className="button-container">
            <button className="button m-t-48 max-width-desktop" type="submit">
              {tc('buttons.save')}
            </button>
          </div>
        </form>
      </FormProvider>
      <TempWarningDialog
        show={tempWarningDialog}
        setShow={setTempWarningDialog}
        handleConfirmation={handleConfirmation}
      />
    </>
  );
};
